<template>
  <v-autocomplete
    :value="value"
    :items="accounts"
    :loading="isSearching"
    :search-input.sync="searchValue"
    item-text="name"
    :item-value="returnObject ? undefined : 'id'"
    :return-object="returnObject"
    :multiple="multiple"
    :chips="multiple"
    :small-chips="multiple"
    :deletable-chips="multiple"
    :label="label"
    :placeholder="placeholder"
    :prepend-icon="prependIcon || undefined"
    :rules="mergedRules"
    :clearable="clearable"
    :disabled="disabled"
    :box="box"
    :hide-details="hideDetails"
    no-data-text="No accounts found"
    no-filter
    @input="onInput"
    @change="onChange"
  >
    <template slot="item" slot-scope="{ item }">
      <v-list-tile-content>
        <v-list-tile-title>{{ item.name }}</v-list-tile-title>
        <v-list-tile-sub-title v-if="midLabel(item)">MID: {{ midLabel(item) }}</v-list-tile-sub-title>
      </v-list-tile-content>
    </template>
  </v-autocomplete>
</template>

<script>
import _ from 'lodash'
import ApiMixin from '@/mixins/api'

// Reusable account selector backed by GET /accounts/selector (server-side,
// debounced search on name OR MID). The endpoint scopes results by the current
// user, so superusers get every account and everyone else gets their own —
// a single source of truth for every account dropdown.
export default {
  name: 'AccountSelect',

  mixins: [ApiMixin],

  props: {
    // v-model. In id mode this is the account id; in returnObject mode it is
    // the full account object.
    value: {
      type: [Number, String, Object, Array],
      default: null
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    // Multi-select. value becomes an array of ids; an empty array means
    // "all accounts" (the synthetic "All" option is not shown in this mode).
    multiple: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: 'Account'
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: true
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    prependIcon: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Type to search accounts...'
    },
    ignoreIds: {
      type: Array,
      default: () => []
    },
    debounceMs: {
      type: Number,
      default: 300
    }
  },

  data () {
    return {
      accounts: [],
      searchValue: '',
      isSearching: false
    }
  },

  computed: {
    mergedRules () {
      let rules = this.rules.slice()
      if (this.required) {
        rules.unshift(v => {
          let ok = this.multiple
            ? Array.isArray(v) && v.length > 0
            : (v !== null && v !== undefined && v !== '')
          return ok || `${this.label} is required`
        })
      }
      return rules
    },

    // Display label of the currently-selected item, used to distinguish a
    // selection-driven search-input change from real user typing. Not applicable
    // in multi mode (v-autocomplete clears the search input after each pick).
    selectedText () {
      if (this.multiple || this.value === null || this.value === undefined || this.value === '') {
        return ''
      }
      if (this.returnObject) {
        return (this.value && this.value.name) || ''
      }
      let match = this.accounts.find(a => String(a.id) === String(this.value))
      return match ? match.name : ''
    }
  },

  watch: {
    searchValue (val) {
      // v-autocomplete sets search-input to the SELECTED item's label whenever a
      // value is picked (e.g. "All", or an account name). That is not a user
      // query — searching for it would wipe the list. Only search on real typing.
      if (val && val === this.selectedText) {
        return
      }
      // v-autocomplete also resets search-input to null itself (its internal
      // setSearch(), fired when the item list updates while unfocused and
      // nothing is selected yet) — real typing always comes through as a
      // string, so null here is Vuetify, not the user. Ignore it.
      if (val === null) {
        return
      }
      this.debouncedSearch(val || '')
    },

    value () {
      this.hydrateSelected()
    },

    ignoreIds () {
      this.search(this.searchValue || '')
    }
  },

  created () {
    this.debouncedSearch = _.debounce(this.search, this.debounceMs)
    // Initial list plus, if a value is preselected, make sure its label is loaded.
    this.search('')
    this.hydrateSelected()
  },

  methods: {
    midLabel (item) {
      if (item.mids && item.mids.length) {
        return item.mids.join(', ')
      }
      return item.mid || ''
    },

    async search (value) {
      this.isSearching = true
      try {
        let result = await this.api({
          url: '/accounts/selector',
          params: {
            search: value,
            ignoreIds: this.ignoreIds,
            limit: 50
          }
        })
        this.accounts = result.data.accounts || []
      } catch (err) {
        console.warn('AccountSelect search failed:', err)
      } finally {
        this.isSearching = false
      }
    },

    // Ensure the currently-selected account(s) are present in the item list so
    // their labels/chips render before the user searches (edit forms, defaulted
    // filters, multi-select with preselected ids).
    async hydrateSelected () {
      if (this.returnObject) {
        // Parent supplies the full object; make sure it is selectable.
        if (this.value && typeof this.value === 'object' && !this.accounts.some(a => a.id === this.value.id)) {
          this.accounts = [this.value, ...this.accounts]
        }
        return
      }

      // Collect the id(s) that need a label loaded.
      let ids = this.multiple
        ? (Array.isArray(this.value) ? this.value : [])
        : [this.value]

      let missing = ids.filter(id =>
        id !== null && id !== undefined && id !== '' &&
        !this.accounts.some(a => String(a.id) === String(id))
      )

      if (!missing.length) {
        return
      }

      try {
        let result = await this.api({
          url: '/accounts/selector',
          params: { ids: missing }
        })
        let fetched = (result.data.accounts || []).filter(a => !this.accounts.some(x => x.id === a.id))
        if (fetched.length) {
          this.accounts = [...fetched, ...this.accounts]
        }
      } catch (err) {
        console.warn('AccountSelect failed to hydrate selected account(s):', err)
      }
    },

    onInput (val) {
      this.$emit('input', val)
    },

    onChange (val) {
      this.$emit('change', val)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/common/AccountSelect.vue